/**
 * The PAGE-SCOPE SURFACE: the app's state and command names, published on `globalThis`.
 *
 * WHY THIS EXISTS. `e2e/` is the migration's acceptance gate and its specs may not be edited
 * — that is the only thing that makes them a regression test rather than a description of
 * whatever the app happens to do today. Those specs drive and inspect the app through bare
 * identifiers inside `page.evaluate`: they read `nodes` and `caps`, they call `openDoc(...)`
 * and `applyPermissions()`, and several of them ASSIGN (`caps = {...}`, `presenceRoster =
 * [...]`, `docMode = 'edit'`) to set up a case the real map cannot be relied on to contain.
 *
 * That worked because `infostation.html` was a classic script, whose top-level `let`/`const`
 * land in the global lexical environment. A module's do not. So the names have to be
 * published deliberately, and this is the one file that does it.
 *
 * WHAT IS PUBLISHED IS THE REAL THING. Every accessor reads and writes the app's own live
 * state; every function is the app's own function. Nothing here is a copy, a stub or a
 * recording — a spec that calls `openDoc()` opens the document, and a spec that reads
 * `nodes[id]` reads the object the app is rendering from. A surface that lied would be worse
 * than no surface: the suite would pass against a fiction.
 *
 * IT SHIPS IN PRODUCTION, deliberately. The specs run against the production build, so a
 * dev-only surface would be untested where it matters. This is the same exposure the vanilla
 * app had for its whole life, made explicit instead of accidental.
 *
 * IT IS NOT AN EXTENSION POINT. Nothing inside `client/src` may read these names — every
 * module imports what it needs. If a new consumer appears inside the app, that is a missing
 * import, not a reason to grow this file.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

type AnyFn = (...args: any[]) => unknown;

const target = globalThis as unknown as Record<string, unknown>;

/**
 * Publish functions under their own names.
 *
 * `writable` so a spec could stub one, and `configurable` so publishing twice (a hot reload,
 * a test harness) replaces rather than throws.
 */
export function exposeFunctions(fns: Record<string, AnyFn>): void {
  exposeValues(fns);
}

/**
 * Publish something that is not a function and does not move: a constant table, or a
 * collection whose IDENTITY is stable while its contents change (`dirtyAiFields` is a Set the
 * app adds to — the spec that reads it must see that same Set, not a copy of it).
 */
export function exposeValues(values: Record<string, unknown>): void {
  for (const [name, value] of Object.entries(values)) {
    Object.defineProperty(target, name, { value, writable: true, configurable: true });
  }
}

/**
 * Publish a mutable variable.
 *
 * An ACCESSOR, not a copied value, and that is the point: `caps = {...}` inside a spec has to
 * reach the variable the app itself reads, exactly as assigning to a classic script's
 * top-level `let` did. A plain data property would take the write and leave the app on its
 * own untouched copy — the suite would then be testing nothing, and passing.
 *
 * Omit `set` for state the app owns and a spec only reads; the assignment then fails loudly
 * in strict mode instead of silently going nowhere.
 */
export function exposeAccessor(name: string, get: () => unknown, set?: (v: any) => void): void {
  Object.defineProperty(target, name, { get, set, configurable: true });
}

/** Publish several accessors at once, from a table of getter/setter pairs. */
export function exposeAccessors(
  entries: Record<string, { get: () => unknown; set?: (v: any) => void }>,
): void {
  for (const [name, d] of Object.entries(entries)) exposeAccessor(name, d.get, d.set);
}
